import React, { useState, useEffect } from 'react';
import { Select, Spin, Alert, Button, Modal } from 'antd';
import {
  LoadingOutlined, UserOutlined, EditOutlined, SnippetsOutlined,
  SettingOutlined, BellOutlined, ThunderboltOutlined, WarningOutlined,
  CheckCircleOutlined, EyeOutlined, ArrowRightOutlined, DeleteOutlined,
} from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import T from './theme';
import OssanEditor from './OssanEditor';
import {
  signatureAvecLogo, urlLogoOrganisme, signatureParDefaut,
} from '../../utils/signatureCourriel';
import logoCarteBrune from '../../assets/images/logo.png';
import { getUser } from '../../variables/constants';

import { avecLibelleTraduit } from '../../i18n/libelles';

const TABS = avecLibelleTraduit([
  { key: 'compte', icon: <UserOutlined /> },
  { key: 'signature', icon: <EditOutlined /> },
  { key: 'templates', icon: <SnippetsOutlined /> },
  { key: 'regles', icon: <SettingOutlined /> },
  { key: 'notifications', icon: <BellOutlined /> },
  { key: 'raccourcis', icon: <ThunderboltOutlined /> },
], 'messagerie:parametres.onglets', { cle: 'key' });

// `id` : clé de traduction de l'action (la touche elle-même ne se traduit pas).

const SHORTCUT_TABLE = avecLibelleTraduit([
  { key: 'n', id: 'nouveau' },
  { key: 'r', id: 'repondre' },
  { key: 'f', id: 'transferer' },
  { key: '/', id: 'rechercher' },
  { key: 'Esc', id: 'fermer' },
  { key: 'e', id: 'archiver' },
  { key: 's', id: 'etoile' },
  { key: 'u', id: 'nonLu' },
  { key: 'g i', id: 'boiteReception' },
  { key: 'g r', id: 'relances' },
], 'messagerie:parametres.raccourcis.actions', { cle: 'id', champ: 'action' });

function PanelHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{subtitle}</div>}
    </div>
  );
}

/* ─── Onglet Compte ─── */
function TabCompte({ nonConfig, navigate, pushToast }) {
  const { t } = useTranslation('messagerie');
  const [suppression, setSuppression] = useState(false);

  /* LA FENETRE DIT CE QUI RESTE, PAS SEULEMENT CE QUI PART. Un agent qui lit
     « supprimer la configuration » craint d'abord pour ses messages : ils sont
     nombreux, ils portent des dossiers, et rien dans le mot « supprimer » ne
     l'en rassure. Cote serveur ils ne risquent rien - la ligne est remise a
     zero et non detruite, justement parce qu'ils la referencent - mais c'est
     ici qu'il faut le dire, au moment ou la question se pose. */
  const supprimer = () => {
    Modal.confirm({
      title: t('parametres.compte.suppression.titre'),
      okText: t('parametres.compte.supprimer'),
      okButtonProps: { danger: true },
      cancelText: t('parametres.compte.suppression.annuler'),
      width: 520,
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          <p style={{ margin: '8px 0' }}>
            {t('parametres.compte.suppression.parametresOublies')}
          </p>
          <p style={{ margin: '8px 0' }}>
            <Trans t={t} i18nKey="parametres.compte.suppression.messagesConserves" components={{ 1: <strong /> }} />
          </p>
        </div>
      ),
      onOk: async () => {
        setSuppression(true);
        try {
          await axiosInstance.delete('/v1/messagerie/config');
          pushToast(<CheckCircleOutlined />, t('parametres.compte.suppression.supprimee'));
          // L'etape suivante est toujours la meme : definir la nouvelle boite.
          // L'ecran Messagerie basculerait de toute facon sur son invitation a
          // configurer, ce qui couterait un clic de plus pour le meme endroit.
          navigate('/messagerie/config');
        } catch {
          pushToast(<WarningOutlined />, t('parametres.compte.suppression.impossible'));
        } finally {
          setSuppression(false);
        }
      },
    });
  };

  return (
    <div>
      <PanelHeader title={t('parametres.compte.titre')} subtitle={t('parametres.compte.sousTitre')} />
      {nonConfig ? (
        <div style={{
          background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10,
          padding: 20, marginBottom: 20,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#9a3412', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <WarningOutlined /> {t('parametres.compte.nonConfiguree')}
          </div>
          <div style={{ fontSize: 13, color: '#c2410c', marginBottom: 16, lineHeight: 1.5 }}>
            {t('parametres.compte.nonConfigureeAide')}
          </div>
          <button
            onClick={() => navigate('/messagerie/config')}
            style={{
              background: T.accent, color: '#fff', border: 'none', borderRadius: 7,
              padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {t('parametres.compte.configurer')} <ArrowRightOutlined />
          </button>
        </div>
      ) : (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#15803d', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckCircleOutlined /> {t('parametres.compte.configuree')}
          </div>
          <div style={{ fontSize: 13, color: '#166534', lineHeight: 1.5 }}>
            {t('parametres.compte.configureeAide')}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            <button
              onClick={() => navigate('/messagerie/config')}
              style={{
                border: '1px solid #16a34a', background: '#fff', color: T.green,
                borderRadius: 7, padding: '7px 16px', fontWeight: 500, fontSize: 13,
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <SettingOutlined /> {t('parametres.compte.modifier')}
            </button>
            <button
              onClick={supprimer}
              disabled={suppression}
              style={{
                border: '1px solid #fecaca', background: '#fff', color: '#dc2626',
                borderRadius: 7, padding: '7px 16px', fontWeight: 500, fontSize: 13,
                cursor: suppression ? 'default' : 'pointer', fontFamily: 'inherit',
                opacity: suppression ? 0.6 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <DeleteOutlined /> {t('parametres.compte.supprimer')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Onglet Signature ─── */
function TabSignature({ pushToast }) {
  const { t } = useTranslation('messagerie');
  const [sig, setSig] = useState('');
  const [preview, setPreview] = useState(false);
  /* L'apercu doit montrer ce que RECEVRA le destinataire, logo compris : sans lui,
     l'agent regle une signature dont il ne verra jamais la vraie forme. */
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /* Vrai quand ce qui s'affiche est une PROPOSITION, pas une signature
     enregistrée. Sans le dire, l'agent croirait sa signature déjà en place et
     quitterait l'écran sans enregistrer. */
  const [propose, setPropose] = useState(false);
  /* L'identité de l'agent, retenue pour le bouton « Insérer mes coordonnées ».
     La proposition par défaut ne s'affiche qu'à qui n'a PAS encore de
     signature : ceux qui en ont déjà une - l'immense majorité après quelques
     semaines - n'avaient aucun moyen d'y ajouter leur poste sans le retaper. */
  const [moi, setMoi] = useState(null);

  useEffect(() => {
    let ignore = false;

    /* Signature et identité en parallèle : la seconde ne sert qu'à PROPOSER une
       signature à qui n'en a pas, et faire attendre l'écran pour une proposition
       serait payer cher un cas qui ne se présente qu'une fois par agent. */
    Promise.all([
      axiosInstance.get('/v1/messagerie/signature').catch(() => null),
      axiosInstance.get('/v1/utilisateurs/me', { skipErrorToast: true }).catch(() => null),
    ]).then(([rSig, rMoi]) => {
      if (ignore) return;
      const identite = rMoi?.data?.data || getUser();
      setMoi(identite);
      const existante = rSig?.data?.data || '';
      if (existante.trim()) {
        setSig(existante);
      } else {
        /* Le champ s'ouvrait vide, et l'immense majorité le laissait vide : les
           messages partaient sans nom ni fonction. La proposition est DÉPOSÉE
           dans l'éditeur, pas enregistrée - l'agent la corrige ou l'efface, et
           rien ne part en son nom sans qu'il l'ait vu. */
        setSig(signatureParDefaut(identite));
        setPropose(true);
      }
    }).finally(() => { if (!ignore) setLoading(false); });

    const org = getUser()?.organismeTrackingId;
    if (!org) { setLogo(logoCarteBrune); return undefined; }
    axiosInstance.get(`/v1/organismes/${org}`, { skipErrorToast: true })
      // Le logo du bureau s'il en a un, sinon celui de la Carte Brune : une
      // signature sans logo laisse une cellule vide et un décalage inexpliqué.
      .then(r => setLogo(urlLogoOrganisme(org, r.data?.data?.logo) || logoCarteBrune))
      .catch(() => setLogo(logoCarteBrune));

    return () => { ignore = true; };
  }, []);

  /* Ajoute le bloc d'identité à la fin de la signature.
     Un avertissement quand le nom y figure déjà : le bouton ne sait pas si
     c'est le même bloc ou une autre mention, et insérer sans le dire
     produirait un doublon que l'agent découvrirait chez son destinataire. */
  function insererCoordonnees() {
    const bloc = signatureParDefaut(moi);
    if (!bloc) return;

    const nom = [moi?.prenoms, moi?.nom]
      .map((p) => (p || '').trim())
      .filter(Boolean)
      .join(' ');
    const dejaPresent = nom && sig.includes(nom);

    const inserer = () => {
      setSig((actuelle) => (actuelle.trim() ? `${actuelle}${bloc}` : bloc));
      setPreview(true);
      pushToast(<CheckCircleOutlined />, t('parametres.signature.coordonneesInserees'));
    };

    if (!dejaPresent) { inserer(); return; }
    Modal.confirm({
      title: t('parametres.signature.doublonTitre'),
      content: t('parametres.signature.doublonTexte'),
      okText: t('parametres.signature.inserer'),
      cancelText: t('parametres.signature.annuler'),
      onOk: inserer,
    });
  }

  async function save() {
    setSaving(true);
    try {
      await axiosInstance.patch('/v1/messagerie/signature', { signature: sig });
      // Enregistrée : ce n'est plus une proposition, et le bandeau doit partir.
      setPropose(false);
      pushToast(<CheckCircleOutlined />, t('parametres.signature.sauvegardee'));
    } catch {
      pushToast(<WarningOutlined />, t('parametres.erreurSauvegarde'));
    } finally { setSaving(false); }
  }

  return (
    <div>
      <PanelHeader
        title={t('parametres.signature.titre')}
        subtitle={logo
          ? t('parametres.signature.sousTitreLogo')
          : t('parametres.signature.sousTitre')}
      />
      {/* POUR CEUX QUI ONT DÉJÀ UNE SIGNATURE. Le bloc d'identité est ajouté à
          ce qu'ils ont écrit, plutôt que de le remplacer : leur signature porte
          souvent une mention que nous ne connaissons pas - un service, une
          référence, une formule de politesse. */}
      {!loading && !propose && signatureParDefaut(moi) && (
        <div style={{ marginBottom: 10 }}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={insererCoordonnees}
            style={{ borderRadius: 8 }}
          >
            {t('parametres.signature.insererCoordonnees')}
          </Button>
        </div>
      )}
      {propose && !loading && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12, borderRadius: 8 }}
          message={t('parametres.signature.proposee')}
          description={t('parametres.signature.proposeeAide')}
        />
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <TabPill active={!preview} onClick={() => setPreview(false)}><EditOutlined /> {t('parametres.signature.editer')}</TabPill>
        <TabPill active={preview} onClick={() => setPreview(true)}><EyeOutlined /> {t('parametres.signature.previsualiser')}</TabPill>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 20, color: T.green }} spin />} />
        </div>
      ) : preview ? (
        <div style={{
          border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, minHeight: 160,
          background: '#fafafa', fontSize: 13.5, lineHeight: 1.6,
        }}>
          {sig
            ? <div dangerouslySetInnerHTML={{ __html: signatureAvecLogo(sig, logo) }} />
            : <span style={{ color: '#9ca3af' }}>{t('parametres.signature.aucune')}</span>}
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 16, paddingTop: 12, fontSize: 12, color: '#9ca3af' }}>
            - Envoyé depuis CBConnect BNCB Togo
          </div>
        </div>
      ) : (
        <OssanEditor
          value={sig}
          onChange={setSig}
          placeholder={t('parametres.signature.placeholder')}
          minHeight={160}
        />
      )}
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <SaveBtn saving={saving} onClick={save} />
      </div>
    </div>
  );
}

/* ─── Onglet Templates ─── */
function TabTemplates({ templates, pushToast }) {
  const { t } = useTranslation('messagerie');
  const [list, setList] = useState(templates || []);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nom: '', sujet: '', corpsHtml: '' });
  const [saving, setSaving] = useState(false);

  function openNew() { setForm({ nom: '', sujet: '', corpsHtml: '' }); setEditing('new'); }
  function openEdit(t) { setForm({ nom: t.nom, sujet: t.sujet, corpsHtml: t.corpsHtml }); setEditing(t.trackingId); }

  async function save() {
    setSaving(true);
    try {
      if (editing === 'new') {
        const r = await axiosInstance.post('/v1/messagerie/templates', form);
        setList(l => [...l, r.data?.data || r.data]);
        pushToast(<CheckCircleOutlined />, t('parametres.modeles.cree'));
      } else {
        await axiosInstance.put(`/v1/messagerie/templates/${editing}`, form);
        setList(l => l.map(t => t.trackingId === editing ? { ...t, ...form } : t));
        pushToast(<CheckCircleOutlined />, t('parametres.modeles.misAJour'));
      }
      setEditing(null);
    } catch {
      pushToast(<WarningOutlined />, t('parametres.erreurSauvegarde'));
    } finally { setSaving(false); }
  }

  async function del(id) {
    try {
      await axiosInstance.delete(`/v1/messagerie/templates/${id}`);
      setList(l => l.filter(t => t.trackingId !== id));
      pushToast(<CheckCircleOutlined />, t('parametres.modeles.supprime'));
    } catch {
      pushToast(<WarningOutlined />, t('parametres.modeles.suppressionImpossible'));
    }
  }

  return (
    <div>
      <PanelHeader title={t('parametres.modeles.titre')} subtitle={t('parametres.modeles.sousTitre')} />
      <button onClick={openNew} style={{
        background: T.accent, color: '#fff', border: 'none', borderRadius: 7,
        padding: '7px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
        fontFamily: 'inherit', marginBottom: 16,
      }}>
        {t('parametres.modeles.nouveauBouton')}
      </button>

      {editing && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20, background: '#fafafa' }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 12 }}>
            {editing === 'new' ? t('parametres.modeles.nouveau') : t('parametres.modeles.modifier')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SettingsInput label={t('parametres.modeles.nom')} value={form.nom} onChange={v => setForm(f => ({ ...f, nom: v }))} placeholder={t('parametres.modeles.nomPlaceholder')} />
            <SettingsInput label={t('parametres.modeles.sujet')} value={form.sujet} onChange={v => setForm(f => ({ ...f, sujet: v }))} placeholder="Ex: RELANCE - Dossier {{numeroSinistre}}" />
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{t('parametres.modeles.corps')}</div>
              <OssanEditor
                value={form.corpsHtml}
                onChange={html => setForm(f => ({ ...f, corpsHtml: html }))}
                placeholder={t('parametres.modeles.corpsPlaceholder')}
                minHeight={160}
              />
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{t('parametres.modeles.variables')} {'{{numeroSinistre}}'} {'{{nomVictime}}'} {'{{dateAccident}}'}</div>
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setEditing(null)} style={{ border: '1px solid #e5e7eb', background: '#fff', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, color: '#6b7280' }}>
              {t('parametres.modeles.annuler')}
            </button>
            <SaveBtn saving={saving} onClick={save} />
          </div>
        </div>
      )}

      {list.length === 0 && !editing && (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>
          {t('parametres.modeles.aucun')}
        </div>
      )}

      {list.map(modele => (
        <div key={modele.trackingId} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start', background: '#fff' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, color: '#111827' }}>{modele.nom}</div>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>{modele.sujet}</div>
          </div>
          <button onClick={() => openEdit(modele)} style={{ border: '1px solid #e5e7eb', background: '#fff', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>{t('parametres.modeles.modifierBouton')}</button>
          <button onClick={() => del(modele.trackingId)} style={{ border: '1px solid #fee2e2', background: '#fff', color: '#dc2626', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>{t('parametres.modeles.supprimerBouton')}</button>
        </div>
      ))}
    </div>
  );
}

/* ─── Onglet Règles ─── */
function TabRegles({ pushToast }) {
  const { t } = useTranslation('messagerie');
  // L'endpoint /regles est un stub cote backend : il repond « mise a jour »
  // sans rien enregistrer. Les regles sont donc en lecture seule (pas de
  // setter, pas de handler onChange) tant qu'aucun stockage n'existe.
  // Libellés : messagerie.json, parametres.regles.<key>, lus au rendu.
  const [rules] = useState([
    { key: 'autoArchive', value: false },
    { key: 'sinisterTag', value: true },
    { key: 'cedeaoAlert', value: true },
  ]);

  return (
    <div>
      <PanelHeader title={t('parametres.regles.titre')} subtitle={t('parametres.regles.sousTitre')} />
      {rules.map(rule => (
        <ToggleRow key={rule.key} label={t(`parametres.regles.${rule.key}`)} value={rule.value} disabled />
      ))}
    </div>
  );
}

/* ─── Onglet Notifications ─── */
function TabNotifications({ pushToast }) {
  const { t } = useTranslation('messagerie');
  // Meme stub que /regles : aucun enregistrement cote backend, donc pas de
  // setter ni de handler onChange - l'affichage reste en lecture seule.
  // Libellés : messagerie.json, parametres.notifications.<key>, lus au rendu.
  const [notifs] = useState([
    { key: 'desktop', value: true },
    { key: 'cedeaoMail', value: true },
    { key: 'soundAlert', value: false },
  ]);
  const [syncFreq, setSyncFreq] = useState('60');

  return (
    <div>
      <PanelHeader title={t('parametres.notifications.titre')} subtitle={t('parametres.notifications.sousTitre')} />
      {notifs.map(n => (
        <ToggleRow key={n.key} label={t(`parametres.notifications.${n.key}`)} value={n.value} disabled />
      ))}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('parametres.notifications.frequence')}</div>
        <Select
          style={{ width: 220 }}
          value={syncFreq}
          disabled
          onChange={setSyncFreq}
          options={[
            { value: '30', label: t('parametres.notifications.frequences.30') },
            { value: '60', label: t('parametres.notifications.frequences.60') },
            { value: '300', label: t('parametres.notifications.frequences.300') },
            { value: '900', label: t('parametres.notifications.frequences.900') },
          ]}
        />
      </div>
    </div>
  );
}

/* ─── Onglet Raccourcis ─── */
function TabRaccourcis() {
  const { t } = useTranslation('messagerie');
  return (
    <div>
      <PanelHeader title={t('parametres.raccourcis.titre')} subtitle={t('parametres.raccourcis.sousTitre')} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#9ca3af', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>{t('parametres.raccourcis.touche')}</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#9ca3af', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>{t('parametres.raccourcis.action')}</th>
          </tr>
        </thead>
        <tbody>
          {SHORTCUT_TABLE.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                <kbd style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 7px', fontSize: 12, background: '#f9fafb', fontFamily: "'JetBrains Mono', monospace" }}>
                  {row.key}
                </kbd>
              </td>
              <td style={{ padding: '8px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #f0f0f0' }}>
                {row.action}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ═══════════════════════════════════════ COMPOSANT PRINCIPAL ═══════════════════════════════════════ */
export default function ParametresMessagerie({ nonConfig, templates, tab, settingsTab, onTabChange, onClose, onToast, pushToast }) {
  const { t } = useTranslation('messagerie');
  const navigate = useNavigate();
  const toast = onToast || pushToast || (() => { });
  const activeTab = tab || settingsTab || 'compte';

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: '#fff', fontFamily: T.font }}>
      {/* Onglets verticaux */}
      <nav style={{
        width: 180, flexShrink: 0, borderRight: '1px solid #e5e7eb',
        background: '#fafafa', padding: '20px 8px',
        display: 'flex', flexDirection: 'column', gap: 2,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', color: '#9ca3af', padding: '4px 10px 12px' }}>
          {t('parametres.titre')}
        </div>
        {TABS.map(onglet => (
          <NavTab key={onglet.key} t={onglet} active={activeTab === onglet.key} onClick={() => onTabChange(onglet.key)} />
        ))}
      </nav>

      {/* Contenu de l'onglet */}
      <div className="cb-scroll" style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
        {activeTab === 'compte' && <TabCompte nonConfig={nonConfig} navigate={navigate} pushToast={toast} />}
        {activeTab === 'signature' && <TabSignature pushToast={toast} />}
        {activeTab === 'templates' && <TabTemplates templates={templates} pushToast={toast} />}
        {activeTab === 'regles' && <TabRegles pushToast={toast} />}
        {activeTab === 'notifications' && <TabNotifications pushToast={toast} />}
        {activeTab === 'raccourcis' && <TabRaccourcis />}
      </div>
    </div>
  );
}

/* ─── Composants utilitaires ─── */
function NavTab({ t, active, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px', borderRadius: 7, border: 'none',
        cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
        fontSize: 13.5, fontWeight: active ? 600 : 400,
        color: active ? T.green : '#374151',
        background: active ? T.greenSoft : hov ? '#f0f0f0' : 'transparent',
        boxShadow: active ? `inset 3px 0 0 ${T.green}` : 'none',
        transition: 'background .1s',
      }}
    >
      <span style={{ display: 'flex' }}>{t.icon}</span> {t.label}
    </button>
  );
}

function ToggleRow({ label, value, disabled, onChange }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '13px 0', borderBottom: '1px solid #f0f0f0',
    }}>
      <span style={{ fontSize: 13.5, color: '#374151' }}>{label}</span>
      <Toggle value={value} disabled={disabled} onChange={onChange} />
    </div>
  );
}

function Toggle({ value, disabled, onChange }) {
  return (
    <div
      onClick={disabled ? undefined : onChange}
      style={{
        width: 40, height: 22, borderRadius: 11, cursor: disabled ? 'not-allowed' : 'pointer',
        background: value ? T.green : '#d1d5db', position: 'relative',
        transition: 'background .2s', flexShrink: 0,
        opacity: disabled ? .5 : 1,
      }}
    >
      <div style={{
        position: 'absolute', top: 2, left: value ? 20 : 2,
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left .2s',
      }} />
    </div>
  );
}

function SaveBtn({ saving, onClick }) {
  const { t } = useTranslation('messagerie');
  return (
    <button
      onClick={onClick} disabled={saving}
      style={{
        background: T.accent, color: '#fff', border: 'none', borderRadius: 7,
        padding: '7px 18px', fontWeight: 600, fontSize: 13,
        cursor: saving ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit', opacity: saving ? .6 : 1,
        display: 'flex', alignItems: 'center', gap: 6,
      }}
    >
      {saving && <Spin indicator={<LoadingOutlined style={{ fontSize: 12, color: '#fff' }} spin />} />}
      {t('parametres.sauvegarder')}
    </button>
  );
}

function SettingsInput({ label, value, onChange, placeholder }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <input
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', border: '1px solid #e5e7eb', borderRadius: 6,
          padding: '7px 10px', fontSize: 13, fontFamily: 'inherit',
          boxSizing: 'border-box', outline: 'none',
        }}
      />
    </div>
  );
}

function TabPill({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      border: '1px solid #e5e7eb', borderRadius: 20,
      padding: '5px 14px', cursor: 'pointer', fontSize: 12.5,
      fontFamily: 'inherit', fontWeight: active ? 600 : 400,
      background: active ? T.green : '#fff',
      color: active ? '#fff' : '#6b7280',
      transition: 'all .15s',
      display: 'inline-flex', alignItems: 'center', gap: 6,
    }}>
      {children}
    </button>
  );
}
